import React, { useState, useEffect, useContext } from "react";
import SurveyContext from "../../context/surveyContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  InformationCircleIcon,
  ArrowUpIcon,
  LightBulbIcon,
  ChartBarIcon,
  ShareIcon,
  DocumentArrowDownIcon,
  SignalIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/solid";
import SideDecorations from "../../components/SideDecorations";
import { useLanguage } from "../../context/languageContext";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../hooks/useApi";
import { Response, Question, CompanyResource, ProcessedResponse, DomainResult, Factor, ResultQuestion, FiveDomain, getFiveDomainLabel } from "../../types";
import { HomeIcon } from "@heroicons/react/24/outline";
import CompanyResourceCard from "../../components/CompanyResourceCard";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import PerformanceBadge from "./PerformanceBadge";
import LoadingAnimation from "./LoadingAnimation";
import HorizontalGauge from "./HorizontalGuage";
import { getActionSteps, getDomainDescription, getExportableResults, getReflectionQuestions, transformResponsesToDomainResult } from "./helpers";
import NavigationWarningModal from "./NavigationWarningModal";
import { useNavigate } from "react-router";
import { scroller } from "react-scroll";
import { useNotifications } from "../../context/notificationContext";

export default function SingleDomainResults() {
  const { contextState } = useContext(SurveyContext);
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [hasSavedResults, setHasSavedResults] = useState<boolean>(false);
  const [emailSubmitting, setEmailSubmitting] = useState<boolean>(false);
  const { getApiData, postApiData } = useApi();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const { data: surveyQuestions } = useQuery<Question[]>({
    queryKey: ["questions"],
    queryFn: () => getApiData(`/survey/questions`),
  });

  const { data: companyResources } = useQuery<CompanyResource[]>({
    queryKey: ["companyResources"],
    queryFn: () => getApiData(`/survey/info/resources`),
    initialData: [],
  });

  const userDomains = Array.from(new Set(contextState.user.processedResponses.map((response) => response.domain)));

  const resultsByDomain = userDomains.map((domain) => ({
    domain,
    result: transformResponsesToDomainResult(
      contextState.user.processedResponses.filter((response) => response.domain === domain),
      surveyQuestions || []
    ),
  }));

  function scrollTo(id: string) {
    scroller.scrollTo(id, {
      duration: 400,
      delay: 0,
      smooth: true,
      offset: -10,
    });
  }

  // Navigation modal handlers
  const handleDownloadAndNavigate = () => {
    if (resultsByDomain.length > 0) {
      exportResults();
    }
    setShowNavigationModal(false);
    if (pendingNavigation) {
      window.location.href = pendingNavigation;
    }
  };

  const handleNavigateWithoutDownload = () => {
    setShowNavigationModal(false);
    if (pendingNavigation) {
      window.location.href = pendingNavigation;
    }
  };

  const handleStayOnPage = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
  };

  const handleEmail = async () => {
    const { response } = await postApiData("/survey/responses/email-results", {
      emailBody: getExportableResults(resultsByDomain.map((r) => r.result!)),
    });

    if (response.ok) {
      notifications.addNotification("Success!", `Results were sent to your email`);
      setHasSavedResults(true);
    } else {
      notifications.addNotification("Error", `An error occurred sending results to your email`, "error");
    }
    setShowNavigationModal(false);
  };

  function exportResults() {
    const printableContent = getExportableResults(resultsByDomain.map((r) => r.result!));

    // Create blob and download
    const blob = new Blob([printableContent], {
      type: "text/html",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Strata_Intel_Results_${new Date().toLocaleDateString("en-US")}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setHasSavedResults(true);
  }

  // Navigation away detection
  useEffect(() => {
    if (hasSavedResults) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      setShowNavigationModal(true);
    };

    const handlePopState = () => {
      setShowNavigationModal(true);
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      const button = target.closest("button");

      if (link && link.href && !link.href.includes(window.location.origin)) {
        e.preventDefault();
        setPendingNavigation(link.href);
        setShowNavigationModal(true);
      } else if (button && button.getAttribute("onclick")?.includes("window.location.href")) {
        e.preventDefault();
        const href = button.getAttribute("onclick")?.match(/href\s*=\s*["']([^"']+)["']/)?.[1];
        if (href) {
          setPendingNavigation(href);
          setShowNavigationModal(true);
        }
      } else if (button && (button.textContent?.includes("View All Domains") || button.textContent?.includes("Learn More"))) {
        e.preventDefault();
        setPendingNavigation("/five-domains");
        setShowNavigationModal(true);
      }
    };

    const REMINDER_SECONDS = 30;
    const reminderTimeout = setTimeout(() => {
      setShowNavigationModal(true);
    }, REMINDER_SECONDS * 1000);

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleClick);

    return () => {
      clearTimeout(reminderTimeout);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClick);
    };
  }, [hasSavedResults]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    if (!contextState.user.processedResponses || contextState.user.processedResponses.length === 0) {
      navigate("/");
    }

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [contextState.user.processedResponses, language, contextState.user.survey_key]);

  const domainInfos = [
    {
      domain: FiveDomain.MEANINGFUL_WORK,
      name: "Meaningful Work",
      iconColor: "from-indigo-500 to-blue-500",
      bgColor: "from-indigo-50 to-blue-50",
      borderColor: "border-blue-200",
      description: "Clear goals and responsibilities transform tasks into fulfillment",
      deepDiveUrl: "meaningful-work",
    },
    {
      domain: FiveDomain.SPIRITUALITY,
      name: "Clinical Spirituality",
      iconColor: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-50 to-cyan-50",
      borderColor: "border-cyan-200",
      description: "Clarity of mission and values",
      deepDiveUrl: "clinical-spirituality",
    },
    {
      domain: FiveDomain.RELATIONSHIPS,
      name: "Relationships",
      iconColor: "from-pink-500 to-rose-500",
      bgColor: "from-pink-50 to-rose-50",
      borderColor: "border-pink-200",
      description: "Strong S.P.A.R.C relationships that builds resilience",
      deepDiveUrl: "relationships",
    },
    {
      domain: FiveDomain.HOBBIES_AND_INTERESTS,
      name: "Hobbies & Interests",
      iconColor: "from-amber-500 to-yellow-500",
      bgColor: "from-amber-50 to-yellow-50",
      borderColor: "border-amber-200",
      description: "Enhance creativity, reduce stress, and improve performance",
      deepDiveUrl: "hobbies-and-interests",
    },
    {
      domain: FiveDomain.PHYSICAL_HEALTH,
      name: "Physical Health",
      iconColor: "from-emerald-500 to-green-500",
      bgColor: "from-emerald-50 to-green-50",
      borderColor: "border-emerald-200",
      description: "Directly impacts cognitive function and energy levels",
      deepDiveUrl: "physical-health",
    },
  ];

  return (
    <AnimatePresence>
      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="min-h-screen">
          <SideDecorations />
          <div className="relative w-full mx-auto max-w-[1500px] px-4 md:px-12 py-12">
            {/* Hero Section */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">Your Results Are Ready!</h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">Discover your personalized insights from the assessment below.</p>
            </motion.div>

            {/* Focus Area and Five Domains Context */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="mb-12">
              <div className="gap-8 items-stretch">
                {/* Five Domains Context - Takes up 1/3 of the row */}
                <div className="max-w-5xl mx-auto">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 h-full flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 mb-3">
                          <SparklesIcon className="h-5 w-5 text-blue-500" />
                          <h2 className="font-semibold text-2xl text-gray-900">The Five Domains</h2>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        Your assessment is based on five key domains that influence happiness and performance. Each month focuses on one or more domains to help you build
                        stress resilience and optimize your performance.
                      </p>
                      <p className="text-gray-800 font-bold leading-relaxed mb-4">This month's domain{userDomains.length > 1 && "s"}:</p>
                      <div className="flex flex-col gap-4">
                        {domainInfos.map((domain, index) => (
                          <div
                            key={index}
                            className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                              userDomains.includes(domain.domain) ? "border-blue-500 bg-blue-50 shadow-md" : "border-gray-200 bg-gray-50 hover:border-gray-300 opacity-60"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3 w-full">
                              <div className="flex items-center justify-center gap-3">
                                <div className="ml-2">
                                  <div className={`w-6 h-6 rounded-md bg-gradient-to-r ${domain.iconColor} flex items-center justify-center`}>
                                    <SparklesIcon className="h-3 w-3 text-white" />
                                  </div>
                                </div>
                                <div>
                                  <h3 className="font-semibold text-gray-900 text-xs">{domain.name}</h3>
                                  <p className="text-xs text-gray-600 leading-tight">{domain.description}</p>
                                </div>
                              </div>
                              {userDomains.includes(domain.domain) && (
                                <button
                                  onClick={() => {
                                    scrollTo(domain.deepDiveUrl);
                                  }}
                                  className="sm:flex hidden text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition-colors text-right items-center gap-2"
                                >
                                  <ArrowRightIcon className="h-4 w-4 " />
                                  View Results
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Divider and Deep Dive Callout */}
            <div className="flex items-center my-12 w-full">
              <hr className="flex-grow border-t-2 border-blue-200" />
              <div className="sm:mx-6 bg-blue-50 border border-blue-200 rounded-xl px-6 py-5 text-center shadow-sm flex flex-col items-center">
                <h3 className="text-xl font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <ChartBarIcon className="h-6 w-6 text-blue-500" />
                  Your Monthly Focus Results
                </h3>
                <p className="text-blue-800 text-base">
                  Below you'll find domain factor results and reflection questions to help you grow in this month's focus area{userDomains.length > 1 && "s"}.
                </p>
              </div>
              <hr className="flex-grow border-t-2 border-blue-200" />
            </div>

            {/* Iterate Over Domains */}
            {resultsByDomain.map(({ domain, result }, domainIndex) => {
              const domainInfo = domainInfos.find((d) => d.domain === domain);

              if (!result) {
                return null;
              }

              return (
                <motion.div
                  id={domainInfo?.deepDiveUrl}
                  key={domainIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: domainIndex * 0.2 }}
                  className="mb-12"
                >
                  <div className={`bg-gradient-to-br rounded-xl sm:p-8 p-4 border-4 ${domainInfo?.borderColor}`}>
                    {/* Domain Description */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between">
                        <h2 className="font-semibold text-2xl mb-2 flex gap-1">
                          <SparklesIcon className="h-5 w-5 text-blue-500" />
                          {getFiveDomainLabel(domain)}
                        </h2>
                      </div>
                      <p className="text-gray-600 leading-relaxed pt-2">
                        {getDomainDescription(domain)}{" "}
                        <span
                          onClick={() => navigate(`/domain-deep-dive?domain=${domainInfo?.deepDiveUrl}`)}
                          className="text-blue-500 hover:text-blue-700 cursor-pointer underline"
                        >
                          Learn More →
                        </span>
                      </p>
                    </div>

                    <div className="sm:grid grid-cols-3 gap-4">
                      {/* Assessment Results */}
                      <div className="sm:col-span-2">
                        <div className="flex items-center gap-2 mb-6">
                          <ChartBarIcon className="h-5 w-5 text-blue-500" />
                          <h3 className="font-semibold text-lg text-gray-900">Your Results</h3>
                        </div>
                        <div className="space-y-6">
                          {result?.factors.map((factor, factorIndex) => (
                            <motion.div
                              key={factorIndex}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: factorIndex * 0.1 }}
                              className="sm:border sm:border-gray-200 rounded-lg sm:p-5"
                            >
                              <h4 className="font-semibold text-lg text-gray-900 mb-2">{factor.name}</h4>
                              <div className="space-y-3">
                                {factor.questions.map((question, index) => (
                                  <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between py-3 px-4 bg-gray-50 rounded-lg gap-3">
                                    <p className="text-sm text-gray-700 flex-1">{question.text}</p>
                                    <div className="flex items-center gap-3">
                                      <HorizontalGauge value={question.value} />
                                      <PerformanceBadge performanceLevel={question.performanceLevel} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-6 sm:col-span-1 sm:mt-0 mt-7">
                        {/* Action Steps */}
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <LightBulbIcon className="h-5 w-5 text-yellow-500" />
                            <h3 className="font-semibold text-lg text-gray-900">How to Improve</h3>
                          </div>
                          <div className="space-y-3">
                            {getActionSteps(domain).map((step, index) => (
                              <div key={index} className="bg-white/60 rounded-lg p-3 border border-blue-100">
                                <div className="flex items-start gap-3">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-blue-600 text-xs font-semibold">{index + 1}</span>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h4>
                                    <p className="text-gray-600 text-xs leading-relaxed">{step.description}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Reflection Questions */}
                        {getReflectionQuestions(domain).length > 0 && (
                          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                            <div className="flex items-center gap-2 mb-6">
                              <QuestionMarkCircleIcon className="h-6 w-6 text-green-500" />
                              <h2 className="font-semibold text-lg text-gray-900">Reflection Questions</h2>
                            </div>

                            <div className="space-y-4">
                              {getReflectionQuestions(domain).map((question, index) => (
                                <div key={index} className="flex items-center gap-3 px-4 py-1">
                                  <div>
                                    <div className="flex h-2 w-2 rounded-full bg-green-500"></div>
                                  </div>
                                  <span className="text-gray-800 text-sm leading-snug">{question}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Callout to save results if not done so yet */}
            {!hasSavedResults && (
              <div className="flex items-center my-12 w-full">
                <hr className="flex-grow border-t-2 border-blue-200" />
                <div className="sm:mx-6 bg-blue-50 border border-blue-200 rounded-xl px-6 py-5 text-center shadow-sm flex flex-col items-center">
                  <h3 className="text-xl font-bold text-blue-900 mb-2 flex items-center gap-2">
                    <ChartBarIcon className="h-6 w-6 text-blue-500" />
                    Don't Lose Your Results!
                  </h3>
                  <p className="text-blue-800 text-base">
                    Your assessment results are anonymous and cannot be retrieved later. We recommend saving your results before leaving this page.
                  </p>
                  <div>
                    {" "}
                    <div className="flex justify-end gap-2 mt-5">
                      <button
                        onClick={() => exportResults()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Download Results</span>
                      </button>
                      {/* <button
                        type="button"
                        className={`${
                          emailSubmitting ? "opacity-50" : ""
                        } inline-flex items-center w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto`}
                        disabled={emailSubmitting}
                        onClick={async () => {
                          setEmailSubmitting(true);
                          await handleEmail();
                          setEmailSubmitting(false);
                        }}
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        {!emailSubmitting ? (
                          "Email to Me"
                        ) : (
                          <svg className="h-5 w-5 animate-spin text-white ml-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                      </button> */}
                    </div>
                  </div>
                </div>
                <hr className="flex-grow border-t-2 border-blue-200" />
              </div>
            )}

            <div className="sm:grid grid-cols-3 gap-4">
              {/* Company Resources Section */}
              {companyResources.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }} className="mb-8 sm:col-span-2">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <SparklesIcon className="h-6 w-6 text-blue-500" />
                      <h2 className="font-semibold text-2xl text-gray-900">Company Specific Resources</h2>
                    </div>
                    <p className="text-gray-600 mb-4">Your dedicated support network and company-specific resources to help you thrive.</p>
                    <div className="flex flex-row gap-4 flex-wrap">
                      {companyResources.map((resource, index) => (
                        <CompanyResourceCard key={index} resource={resource} />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Learn More Section */}
              {/* <div
                className={`bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white ${companyResources.length > 0 ? "sm:col-span-1" : "sm:col-span-3"}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <SparklesIcon className="h-6 w-6 text-white" />
                  <h2 className="font-semibold text-2xl">Learn More</h2>
                </div>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Explore All Domains</h3>
                    <p className="text-indigo-100 text-sm mb-4">
                      Learn about all five domains and understand how they work together to build stress resilience and optimize performance.
                    </p>
                    <button
                      onClick={() => {
                        if (!hasSavedResults) {
                          setPendingNavigation("https://learn.strataintel.com/domains");
                          setShowNavigationModal(true);
                        } else {
                          window.open("https://learn.strataintel.com/domains", "_blank");
                        }
                      }}
                      className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition-colors flex items-center gap-2"
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                      Click to Learn More
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </motion.div>
      )}

      <NavigationWarningModal
        open={showNavigationModal}
        handleDownloadAndNavigate={handleDownloadAndNavigate}
        handleNavigateWithoutDownload={handleNavigateWithoutDownload}
        handleStayOnPage={handleStayOnPage}
        handleEmail={handleEmail}
        isNavigationType={!!pendingNavigation}
      />
    </AnimatePresence>
  );
}
